const target = Symbol('__target__')

import { SDKEvent } from '.'
import { MissingEventBusError } from '../errors'

type CustomError = (error: Error ) => void
type CustomEventError = (error: ErrorEvent) => unknown | void
type CustomEventListener = (event: CustomEvent | Event) => void
type CustomEventListenerOrEventListener = EventListenerOrEventListenerObject | CustomEventListener | CustomError | CustomEventError | unknown

export interface EventBus {
  get target(): EventTargetContext
  addEventListener(type: string | SDKEvent | (() => SDKEvent), listener: CustomEventListenerOrEventListener, options?: boolean | AddEventListenerOptions, clean?: boolean): void
  dispatchEvent(event: Event | CustomEvent): void
  removeEventListener(type: string | SDKEvent, callback: CustomEventListenerOrEventListener, options?: boolean | EventListenerOptions): void
  removeEventListeners(): void
}

export class EventsTrait {
  get events(): EventBus {
    throw new MissingEventBusError
  }

  /**
   *
   * @param type - Event name
   * @param listener - Callback
   * @param options - Event listener options
   */
  on(type: string | SDKEvent, listener: CustomEventListenerOrEventListener, options?: boolean | AddEventListenerOptions): EventsTrait {
    this.events && this.events.addEventListener(String(type), listener, options, true)
    return this
  }

  /**
   *
   * @param type - Event name
   * @param listener - Callback
   * @param options - Event listener options
   */
  once(type: string | SDKEvent, listener: CustomEventListenerOrEventListener): EventsTrait {
    this.events && this.events.addEventListener(String(type), listener, { once: true })
    return this
  }

  /**
   *
   * @param type - Event name
   * @param listener - Callback
   * @param options - Event listener options
   */
  off(type?: string | SDKEvent, listener?: CustomEventListenerOrEventListener, options?: boolean | AddEventListenerOptions): EventsTrait {
    if (!type) {
      this.events && this.events.removeEventListeners()
    } else {
      this.events && this.events.removeEventListener(String(type), listener as CustomEventListenerOrEventListener, options)
    }

    return this
  }
}

/**
 * Polyfill for old safari versions
 */
export type EventTargetContext = EventTarget | Element

export class EventTarget implements EventTarget, EventBus {
  get target(): EventTargetContext {
    return this[target as never]
  }

  constructor(context?: EventTargetContext, tag = 'div') {
    const key = (Symbol.toStringTag || Symbol('toStringTag'))
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const self = this as any
    const fallbackEventTarget = document.createElement(tag)

    self[key] = 'EventTarget'

    if (context) {
      self[target] = context
    } else {
      try {
        self[target] = window.EventTarget ? new window.EventTarget : fallbackEventTarget
      } catch {
        self[target] = fallbackEventTarget
      }
    }
  }

  /**
   *
   * @param type - Event name
   * @param listener - Callback
   * @param options - Event listener options
   */
  addEventListener(type: string | SDKEvent, listener: CustomEventListenerOrEventListener, options?: boolean | AddEventListenerOptions, clean?: boolean): void {
    if (clean) this.removeEventListener(String(type), listener, options)
    this.target.addEventListener(String(type), listener as never, options)
  }

  dispatchEvent(event: Event | CustomEvent) {
    this.target.dispatchEvent(event)
  }

  removeEventListener(type: string | SDKEvent, listener: CustomEventListenerOrEventListener, options?: boolean | EventListenerOptions): void {
    this.target.removeEventListener(String(type), listener as never, options)
  }

  removeEventListeners() {
    const element = this[target as never] as HTMLElement
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const self = this as any
    if (element instanceof HTMLElement) {
      element.remove && element.remove()
      delete self[target]
      self[target] = document.createElement('div')
    } else {
      delete self[target]
      self[target] = new window.EventTarget
    }
  }
}

export const createEventBus = (context?: EventTargetContext): EventBus => new EventTarget(context)

export default createEventBus()


